;(() => {
  // utils
  function anyNonNull(...args) {
    return args.find((arg) => arg != null)
  }
  function debounceEvery(func, timeout = 100) {
    let all_args = []
    let timer
    return (...args) => {
      clearTimeout(timer)
      all_args.push(args)
      timer = setTimeout(() => {
        func.apply(this, all_args)
      }, timeout)
    }
  }
  
  const domParser = new DOMParser()

  const JSON_HEADERS = {
    accept: 'application/json',
    'content-type': 'application/json',
  }
  
  class RatingApiBase {
    /** @type {Record<string, any>} */
    _CACHE = {}
    
    /** @type {string[]} */
    requiredAttributes

    /**
     * @returns {Promise<any>}
     */
    async fetch() {
      throw new Error('Not implemented')
    }
  }

  class StampedImpl extends RatingApiBase {
    requiredAttributes = ['api-key', 'shop-permanent-domain', 'pid']
    constructor() {
      super()
      
      const self = this
      this.fetch = debounceEvery((...allCalls) => {
        const fistArg = allCalls[0]
        const reqAttrs = fistArg[1]
        const apiKey = reqAttrs['api-key']
        const sId = reqAttrs['store-hash-id']
        const myshopifyUrl = reqAttrs['shop-permanent-domain']

        let pids = new Set()

        // Actual fn signature: `fetch(productId, { 'api-key': '', 'stamped-sid': '' }, resolve?)`
        allCalls.forEach(([pid, requiredAttributes, resolve]) => {
          if (typeof pid === 'number') {
            pid = pid.toString()
          }

          if (typeof resolve === 'function' && self._CACHE[pid]) {
            resolve(self._CACHE)
          } else {
            pids.add(String(pid))
          }
        })

        pids = Array.from(pids)

        const productIds = pids.map((pid) => ({ productId: pid }))
        const body = {
          apiKey,
          sId,
          productIds,
          storeUrl: myshopifyUrl,
        }

        // console.log("Stamped batched fetch...", body.productIds)
        return fetch('https://stamped.io/api/widget/badges', {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify(body),
        })
          .then((r) => r.json())
          .then((list) => {
            const byId = list.reduce((o, result) => ((o[result.productId] = result), o), {})

            Object.assign(this._CACHE, byId)

            return this._CACHE
          })
          .then((allById) => {
            allCalls.forEach(([pid, requiredAttributes, resolve]) => {
              if (typeof resolve === 'function') {
                resolve(this._CACHE)
              }
            })

            return this._CACHE
          })
      })
    }
  }
  
  class YotpoImpl extends RatingApiBase {
    requiredAttributes = ['api-key', 'shop-permanent-domain', 'pid']
    
    async fetch (pid, reqAttrs, resolve) {
      const apiKey = reqAttrs['api-key']
      const myshopifyUrl = reqAttrs['shop-permanent-domain']

      const cached = this._CACHE[pid]

      if (cached !== undefined) {
        return typeof resolve === 'function' ? resolve(this._CACHE) : cached
      }

      const promise = fetch(
        `https://api-cdn.yotpo.com/v2/bottom_lines/store/${apiKey}/product/${pid}`,
        {
          headers: {
            accept: 'application/json',
            'x-custom-integration': 'true',
          },
        }
      )
        .then((r) => r.json())
        .then((r) => {
          if (r.bottomline) {
            const rinfo = r.bottomline

            // `r.bottomline` schema
            /*
            {
              "totalReview": 0,
              "averageScore": 0,
              "totalOrganicReviews": 0,
              "organicAverageScore": 0,
              "starDistribution": null,
              "customFieldsBottomline": null
            }
            */
            const count = rinfo.totalReviews
            const data = {
              count: anyNonNull(rinfo.totalReviews, rinfo.totalReview, rinfo.totalOrganicReviews),
              rating: anyNonNull(rinfo.averageScore, rinfo.organicAverageScore),
            }

            this._CACHE[pid] = data
            if (typeof resolve === 'function') resolve(this._CACHE)
            return this._CACHE
          }
        })
        .catch(e => {
          this._CACHE[pid] = null
          if (typeof resolve === 'function') resolve(this._CACHE)
          return this._CACHE
        })

      this._CACHE[pid] = promise
      return promise
    }
  }

  class JudgemeImpl extends RatingApiBase {
    requiredAttributes = ['api-key', 'shop-permanent-domain', 'pid']
    
    async fetch (pid, reqAttrs, resolve) {
      const apiKey = reqAttrs['api-key']
      let myshopifyUrl = reqAttrs['shop-permanent-domain']
      const dev  = myshopifyUrl === ('sendero-provisions-dev.myshopify.com')
      if (dev) myshopifyUrl = 'senderopc.myshopify.com'

      const cached = this._CACHE[pid]

      if (cached !== undefined) {
        return typeof resolve === 'function' ? resolve(this._CACHE) : cached
      }

      // CORS blocked :/ 
      // base url + query params
      let url = `https://api.judge.me/api/v1/widgets/preview_badge`
      url += `?shop_domain=${myshopifyUrl}&api_token=${apiKey}&external_id=${pid}`

      try {
        const promise = fetch(url, {
            headers: {
              accept: 'application/json',
            },
          })
          .then((r) => r.json())
          .then((r) => {
            if (!r || !r.badge) {
              const data = getTestRating(!dev) || { count: testCount ?? 0, rating: testRating ?? 0 }
              this._CACHE[pid] = data
              if (typeof resolve === 'function') resolve(this._CACHE)
              return data
            }
  
            const doc = domParser.parseFromString(r.badge, 'text/html')
            const div = doc.querySelector('[data-average-rating][data-number-of-reviews]')
            const rating = +div.getAttribute('data-average-rating') || 0
            const reviews_count = +div.getAttribute('data-number-of-reviews') || 0
            // const questions_count = +div.getAttribute('data-number-of-questions') || 0
            // const text = doc.querySelector('.jdgm-prev-badge__text')?.textContent?.trim() || ''
            
            const data = {
              count: reviews_count,
              rating: rating,
            }
  
            this._CACHE[pid] = data
            if (typeof resolve === 'function') resolve(this._CACHE)
            return this._CACHE
          })
          .catch(e => {
            this._CACHE[pid] = null
            if (typeof resolve === 'function') resolve(null)
            return null
          })
  
        this._CACHE[pid] = promise
        return promise
      } catch(e) {
        // cors error or something
        this._CACHE[pid] = null
        if (typeof resolve === 'function') resolve(this._CACHE)
        return this._CACHE
      }
    }
  }

  const RATING_APPS_API = {
    stamped: new StampedImpl(),
    yotpo: new YotpoImpl(),
    judgeme: new JudgemeImpl(),
  }

  const SUPPORTED_RATING_APPS = Object.keys(RATING_APPS_API)

  // const _debug = new URL(window.location).searchParams.has('_debug')
  // if (!_debug) return

  class ProductRatingBadge extends HTMLElement {
    static template = document.getElementById('template--product-rating-badge')
    _tag = 'product-rating-badge'
    constructor() {
      super()
      
      this._ac = new AbortController()
    }

    get _dynamicTextNodes() {
      if (!this._dynamicTextNodesCached) {
        this._dynamicTextNodesCached = [this]
          .concat(Array.from(this.querySelectorAll('[x-text]')))
          .filter((node) => node.getAttribute('x-text'))
      }
      return this._dynamicTextNodesCached
    }

    async connectedCallback() {
      await Promise.resolve()
      if (!this.isConnected) return

      const viaTemplate = this.getAttribute('data-via-template') === 'true'
      if (viaTemplate) {
        const template = ProductRatingBadge.template
        if (!template) {
          console.warn('[product-rating-badge] Missing template for product-rating-badge')
          return
        }
        const cloned = template.content.cloneNode(true)?.firstElementChild
        if (!cloned) {
          console.warn('[product-rating-badge] Missing template for product-rating-badge')
          return
        }
        cloned.removeAttribute('data-via-template')
        const pid = this.getAttribute('pid') || this.getAttribute('data-product-id')
        cloned.setAttribute('pid', pid)
        cloned.querySelectorAll('a').forEach(a => a.removeAttribute('href'))
        this.replaceWith(cloned)
        return
      }

      if (!this._ac || this._ac.signal.aborted) this._ac = new AbortController()

      this.appName = this.getAttribute('app-name')
      this.api = this.appName ? RATING_APPS_API[this.appName] : null
      if (!this.api) {
        console.warn(
          `${this._tag} Missing or unsupported app type: ${
            this.appName
          }. \n Supported apps: "${SUPPORTED_RATING_APPS.join(', ')}"`
        )
        return
      }

      this._$refName = this.getAttribute('x-ref') || 'data'
      this._attrs_valid = true
      this.requiredAttributes = this.api.requiredAttributes.reduce((acc, attr) => {
        let value = this.getAttribute(attr)
        if (!value && attr === 'pid') {
          value = this.getAttribute('data-product-id')
        }

        if (!value) {
          this._attrs_valid = false
        }

        acc[attr] = value
        return acc
      }, {})

      if (!this._attrs_valid) {
        console.log(`${this._tag} Missing required attribute: "${this.api.requiredAttributes}"\n`, this)
        return
      }

      this.productId = this.requiredAttributes.pid
      this.log_prefix = `[${this._tag}] ${this.productId} =>  `

      this.style.display = ''
      this.fetchRating()
      this.setupClickHandler()
    }

    async disconnectedCallback() {
      await Promise.resolve()
      if (this.isConnected) return
      this._ac?.abort()
    }

    async fetchRating() {
      if (this._attrs_valid === false) return

      const cache = await new Promise((resolve) => {
        this.api.fetch(this.productId, this.requiredAttributes, resolve)
      })
      
      if (this._ac.signal.aborted) return
      
      this.result = await cache[this.productId]
      // console.log('Done fetching badge ... ', this)

      this.updateNode()
    }

    updateNode() {
      if (!this.result) return
      const { rating, count } = this.result
      this.style.setProperty('--rating', rating)
      this.setAttribute('rating-count', count)
      this.setAttribute('data-rating-count', count)

      // this.setAttribute('title', count <= 0 ? 'No reviews yet' : `${rating} out of 5`)
      this.setAttribute('title', count <= 0 ? 'Write Review' : `${rating} out of 5`)
      this._dynamicTextNodes.forEach((node) => {
        const maybeFn = getFnFromNode(node, 'x-text', this._$refName)
        if (typeof maybeFn === 'function') {
          try {
            let resultingText = maybeFn(this.result)
            node.textContent = resultingText
          } catch (e) {
            console.error(e)
          }
        }
      })
    }
    
    setupClickHandler() {
      const gotoLink = this.querySelector('a[data-goto-link]')
      if (!gotoLink) return
      const href = gotoLink.getAttribute('href')
      if (!href || !href.startsWith('#')) return
      // const reviewsTabBtn = document.querySelector('.horizontal-tab-menu [data-tab="reviews"]')
      const reviewsTabBtn = document.querySelector('#wrap_des_pr [data-reviews-tab-toggle]')
      if (reviewsTabBtn) {
        this.clickHandler = (e) => {
          e.preventDefault()
          e.stopPropagation()
          reviewsTabBtn.click()
          // scroll into view reviewsTabBtn
          reviewsTabBtn.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
        gotoLink.addEventListener('click', this.clickHandler, { signal: this._ac.signal })
      }
    }
  }

  if (!window.customElements.get('product-rating-badge')) {
    window.customElements.define('product-rating-badge', ProductRatingBadge)
  }

  function getFnFromNode(node, fnBodyAttr, dataName = 'data') {
    const fnCacheKey = `_fn_body_${fnBodyAttr}`
    let fn = node[fnCacheKey]
    if (fn) return fn
    const fncBody = node.getAttribute(fnBodyAttr)
    if (fncBody) {
      fn = new Function(dataName, `return (${fncBody})`)
      node[fnCacheKey] = fn
      return fn
    }
  }
})()

let _test_count = 0
function getTestRating(empty = true) {
  if (empty) {
    return {
      rating: 0, 
      count: 0,
    }
  }
  _test_count++

  empty = (_test_count > 1 && _test_count % 3 === 0)

  // random int between 0 to 10
  const count =empty ? 0 :  Math.floor(Math.random() * 11)
  // random rating between 0 to 5 including decimal updto 1, 2.5, 3.2, 3.0 etc
  const rating = empty? 0 :  Math.min(Math.floor(Math.random() * 6) + Math.floor(Math.random() * 10) / 10, 5)

  return {
    rating: rating,
    count: count,
  }
}

/*
<script>
function __stampedsetglobals() {
  if (!window.StampedGlobalOptions) window.StampedGlobalOptions = {}
  if (!window.StampedGlobalOptions.apiKey) window.StampedGlobalOptions.apiKey = 'b678a98b-dbaf-4ab1-9e38-f98a372a06be'
  if (!window.StampedGlobalOptions.sId) window.StampedGlobalOptions.sId = '301731'
  if (!window.StampedGlobalOptions.storeUrl) window.StampedGlobalOptions.storeUrl = '{{ shop.permanent_domain }}'
}
function __afterstampedscriptload() {
  __stampedsetglobals()
  requestAnimationFrame(() => {
    __stampedsetglobals()
  })

  setTimeout(() => {
    __stampedsetglobals()
  }, 100)
}
</script>

<!-- Stamped -->
<script
async
defer="defer"
type="text/javascript"
data-api-key="{{ shop.metafields.stamped.api_public | default: 'b678a98b-dbaf-4ab1-9e38-f98a372a06be' }}"
id="stamped-script-widget"
src="https://cdn1.stamped.io/files/widget.min.js?{{ shop.permanent_domain }}"
onload="__afterstampedscriptload()"
>
</script>
<!-- End Stamped -->
*/
